// Pinia Store
import { getActivePinia } from 'pinia'
import type { FetchError } from 'ofetch'
import type { SupportedCountry } from '~/index'
import type { UserAuth } from '~/types'
import { useLogger } from '#layers/base/composables/useLogger'
import type { ApiShopCustomer } from '#layers/base/server/utils/api-types'
import { runWithAuthRetry } from '~/composables/useWithAuthRetry'

export const VAT_NUMBER_REGEX = /^(DE|FR)?\d{9}$/

export const HYBRID_SEARCH_FEATURE_KEY = 'hybrid-search-2026'

export interface UserData {
  academicTitle: string | null
  acceptAgb: boolean
  acceptNewsletter: boolean
  billingCity: string | null
  billingPostalCode: string | null
  billingStreetName: string | null
  billingStreetNumber: string | null
  city: string
  clinicChain: {
    handle: string
    name: string
  } | null
  clinicChainMembershipSelfFilled: string
  clinicName: string
  clinicShortName: string | null
  country: string
  email: string
  featureKeys: string[]
  firstname: string
  handle: string
  lastname: string
  officialClinicNumber: string | null
  phone: string
  postalCode: string
  dateCreated: string
  streetName: string
  streetNumber: string
  vatNumber: string | null
  vetDiplomaNumber: string
  vetNarcoticsNumber: string | null
  accountType: string
  isIbanAvailable?: boolean | null
  iban?: string | null
  tosPayment?: boolean
  pharmacyRegistrationCertificateKey?: string
  pharmacyRegistrationCertificateDateUploaded?: string
}

interface ApiError {
  code?: string
  errors?: Record<string, string>
  data?: {
    code: string
    errors: Record<string, string>
  }
  message?: string
  stack?: string
  statusCode?: number
  statusMessage?: string
  url?: string
}

interface RegisterFormData {
  clinicName: string
  streetName: string
  streetNumber: string
  postalCode: number
  city: string
  billingStreetName: string
  billingStreetNumber: string
  billingPostalCode: string
  billingCity: string
  clinicChainMembershipSelfFilled: string
  officialClinicNumber: string
  academicTitle: string
  firstname: string
  lastname: string
  vetDiplomaNumber: string
  phone: string
  username: string
  password: string
  countrycode: SupportedCountry
  hearAboutUs?: string
  hearAboutUsText?: string
  acceptAgb: boolean
  acceptNewsletter: boolean
}

interface LoginResponse {
  handle: string
  email: string
  accessToken: string
}

export const useUserStore = defineStore('user', () => {
  const { baseURL, mainDomain } = useCountryConfig()
  const failoverCountries = (Object.keys(useAppConfig().countryConfig) as SupportedCountry[])
    .map(countryCode => useCountryConfig(countryCode))
    .filter(country => !country.disabled && country.mainDomain === mainDomain && country.baseURL !== baseURL)
    // This is a hack to ensure that we only log in against the locale system on local!
    .filter(() => baseURL !== '')
  const log = useLogger('useUserStore')

  const isAuthenticated = ref(false)
  const gpoHandle = ref('none')
  const customerHandle = ref<string | null>(null)
  const accessToken = ref(null)
  const isTestUser = ref(false)

  const {
    data: auth,
    pending: isAuthPending,
    refresh: refreshAuth,
  } = useFetch<UserAuth>('/api/auth', {
    baseURL,
    onResponse: ({ response }) => {
      if (!response.ok) return
      const data = response?._data ?? {}
      isAuthenticated.value = !!data.accessToken
      accessToken.value = data.accessToken
      customerHandle.value = data.handle
    },
    onResponseError({ response }) {
      log.warn('User is not loggedIn -> log out user', { responseStatus: response.status })
      // if the customerHandle is undefined, the user is not logged in. This check is needed to prevent the user from being logged out when the user is not logged in.
      if (unref(customerHandle) != null) {
        _resetAndRedirectToLogin().then(() => _logError('User session is expired', { statusCode: response.status }))
      }
    },
  })

  let authRefreshInFlight: Promise<unknown> | null = null

  /**
   * Single-flight `/api/auth` refresh: coalesces concurrent callers onto one
   * shared promise so parallel 401 handlers don't trigger redundant refreshes.
   */
  function reloadAuth(): Promise<unknown> {
    if (authRefreshInFlight) return authRefreshInFlight
    authRefreshInFlight = Promise.resolve(refreshAuth()).finally(() => {
      authRefreshInFlight = null
    })
    return authRefreshInFlight
  }

  const query = computed(() => ({ handle: unref(customerHandle) }))
  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const abortIfNotAuthenticated = {
    onRequest({ options }: { options: { signal?: AbortSignal } }) {
      if (!unref(accessToken)) {
        options.signal = AbortSignal.abort()
        isAuthenticated.value = false
      }
    },
  }
  const {
    data: userData,
    pending: isUserDataPending,
  } = useFetch<UserData>('/api/intern/customers', {
    headers: headerFields,
    query,
    baseURL,
    watch: [accessToken],
    ...abortIfNotAuthenticated,
    onResponse: ({ response }) => {
      const data = response?._data ?? {}
      gpoHandle.value = data.clinicChain?.handle ?? 'none'
      isTestUser.value = data.accountType === 'TEST'
    },
  })

  function validateVatNumber(vatNumber: string) {
    if (!vatNumber) {
      return {
        isValid: false,
        errorMessage: 'errors.code.InvalidVatNumber',
      }
    }

    const isValid = VAT_NUMBER_REGEX.test(vatNumber)
    return {
      isValid,
      errorMessage: isValid ? '' : 'errors.code.InvalidVatNumber',
    }
  }

  function validateIban(iban: string) {
    // TEST IBANS
    // wrong: DE18200505501001402682
    // invalid: DE55370400440532014000
    // valid: DE89370400440532013000

    if (!iban) {
      return {
        isValid: true,
        errorMessage: '',
      }
    }

    const isValid = /^(?:(?:IT|SM)\d{2}[A-Z]\d{22}|CY\d{2}[A-Z]\d{23}|NL\d{2}[A-Z]{4}\d{10}|LV\d{2}[A-Z]{4}\d{13}|(?:BG|BH|GB|IE)\d{2}[A-Z]{4}\d{14}|GI\d{2}[A-Z]{4}\d{15}|RO\d{2}[A-Z]{4}\d{16}|KW\d{2}[A-Z]{4}\d{22}|MT\d{2}[A-Z]{4}\d{23}|NO\d{13}|(?:DK|FI|GL|FO)\d{16}|MK\d{17}|(?:AT|EE|KZ|LU|XK)\d{18}|(?:BA|HR|LI|CH|CR)\d{19}|(?:GE|DE|LT|ME|RS)\d{20}|IL\d{21}|(?:AD|CZ|ES|MD|SA)\d{22}|PT\d{23}|(?:BE|IS)\d{24}|(?:FR|MR|MC)\d{25}|(?:AL|DO|LB|PL)\d{26}|(?:AZ|HU)\d{27}|(?:GR|MU)\d{28})$/.test(
      iban.replace(/\s/g, ''),
    )
    return {
      isValid,
      errorMessage: isValid ? '' : 'cart.error.iban.invalid',
    }
  }

  function _trackUserEvent(event: string, data?: { email: string, handle: string }) {
    const gtm = useGtm()
    gtm?.trackEvent({ event, customer_handle: data?.handle })
  }

  function _logError(logMessage: string, error?: any) {
    const { $i18n, $storeErrorLog } = useNuxtApp()
    $storeErrorLog(
      'stores/user.ts',
      logMessage,
      $i18n.t(error.data?.code ? `errors.code.${error.data.code}` : 'errors.page.unknown.subtitle'),
      error,
    )
  }

  async function load() {
    return auth.value
  }

  const sendLogin = (username: string, password: string, baseURL: string) =>
    $fetch<LoginResponse>('/api/login', { method: 'POST', body: { username, password }, baseURL })

  async function login(username: string, password: string) {
    const [error, initialLoginData] = await attempt(sendLogin(username, password, baseURL))
    if (!error) {
      _trackUserEvent('login', initialLoginData)
      await reloadAuth()
      return { success: true, country: undefined }
    }
    if (failoverCountries.length > 0) {
      log.debug('User failed to login (initial attempt)', { baseURL, username })
      const [finalError, finalLoginData] = await attempt(Promise.any(
        failoverCountries.map(country => sendLogin(username, password, country.baseURL).then(loginData => ({ country, loginData }))),
      ))
      if (!finalError) {
        // TODO: check if the reloadAuth is needed here
        log.debug('User successfully logged in to a failover country', { finalLoginData })
        _trackUserEvent('login', finalLoginData.loginData)
        return { success: true, country: finalLoginData.country }
      }
    }
    log.debug('User failed to login', { username })
    _logError('User failed to login', error)
    return { success: false }
  }

  async function register(formData: RegisterFormData) {
    const { baseURL } = useCountryConfig(formData.countrycode)
    const [error, registrationData] = await attempt<FetchError<{ errors: Record<string, string> }>, LoginResponse | 'UNCONFIRMED'>(
      $fetch('/api/register', { method: 'POST', body: formData, baseURL }),
    )
    if (error) {
      log.warn('Unable to register user', { error })
      return error
    }
    _trackUserEvent('sign_up', registrationData !== 'UNCONFIRMED' ? registrationData : { email: formData.username, handle: 'UNCONFIRMED' })
    return registrationData
  }

  async function update(formData: ApiShopCustomer): Promise<{ status: number, error?: ApiError }> {
    if (!unref(accessToken)) return { status: 401 }
    try {
      const handle = unref(customerHandle)
      const doUpdate = () => $fetch<ApiShopCustomer>(`/api/intern/customers/${handle}`, {
        method: 'PUT',
        body: formData,
        headers: headerFields.value,
        baseURL,
      })
      const response = await runWithAuthRetry(doUpdate, {
        reloadAuth,
        getAccessToken: () => accessToken.value,
        log,
      })
      userData.value = {
        ...(userData.value ?? {} as UserData),
        ...response,
      }
      return {
        status: 200,
      }
    }
    catch (error: any) {
      _logError('Could not update', error)
      return {
        status: error.statusCode,
        error: error.data,
      }
    }
  }

  async function getProfileData() {
    return userData.value
  }

  const sendForgotPassword = (username: string, baseURL: string) =>
    $fetch('/api/forget-password', { method: 'POST', body: { username }, baseURL })

  async function forgotPassword(username: string): Promise<boolean> {
    const [error] = await attempt(sendForgotPassword(username, baseURL))
    if (!error)
      return true
    if (failoverCountries.length > 0) {
      log.debug('User failed to initiate password reset (initial attempt)', { baseURL, username })
      const [finalError] = await attempt(Promise.any(
        failoverCountries.map(country => sendForgotPassword(username, country.baseURL)),
      ))
      if (!finalError)
        return true
    }
    log.debug('User failed to initiate password reset', { username })
    return false
  }
  async function resetPassword(verificationCode: string, username: string, password: string): Promise<any> {
    try {
      return $fetch('/api/reset-password', {
        method: 'POST',
        body: { verification_code: verificationCode, username, password },
        baseURL,
      })
    }
    catch (error: any) {
      _logError('Could not reset password', error)
      // If the status code is not Server Error (a known error like bad request or cognito error like user does not exists) we want the browser to continue
      return error.data
    }
  }

  async function logout() {
    try {
      await $fetch('/api/logout', { baseURL })
      await _resetAndRedirectToLogin()
    }
    catch (error: any) {
      // If logout returns 401, session is already invalid - still log out locally
      if (error.statusCode === 401) {
        await _resetAndRedirectToLogin()
      }
      else {
        _logError('Could not logout', error)
      }
    }
  }

  /**
   * This function is only to use for updating the IBAN only e.g. in the cart.
   * Do not use this at the profile page
   *
   * @param value - IBAN
   * @param consent - value from check box
   */
  async function updateIban(value: string, consent: boolean): Promise<{ success: boolean, error?: { code: string, field: string, message: string }, fieldErrors?: Record<string, string> }> {
    try {
      await $fetch<unknown>('/api/v2/profile', {
        method: 'PUT',
        body: { iban: value, consent },
        baseURL,
        onResponseError({ response }) {
          if (response.status === 401) void reloadAuth()
        },
      })
      if (userData.value)
        userData.value.isIbanAvailable = true
      return { success: true }
    }
    catch (error: unknown) {
      _logError('Could not update IBAN', error)
      const fetchError = error as FetchError<{ errors?: Record<string, string> }>
      const apiErrors = fetchError?.response?._data?.errors

      if (apiErrors?.iban === 'InvalidIban') {
        return {
          success: false,
          error: {
            code: 'InvalidIban',
            field: 'iban',
            message: 'cart.error.iban.invalid',
          },
        }
      }

      if (apiErrors && Object.keys(apiErrors).length > 0) {
        return {
          success: false,
          fieldErrors: apiErrors,
        }
      }

      return {
        success: false,
        error: {
          code: 'UnknownError',
          field: 'iban',
          message: 'cart.error.unknown',
        },
      }
    }
  }

  async function _resetAndRedirectToLogin() {
    const localePath = useLocalePath()
    _trackUserEvent('logout')
    await $reset()
    navigateTo(localePath('/login'), { replace: true })
  }

  function $reset() {
    userData.value = null
    auth.value = null
    accessToken.value = null
    customerHandle.value = null
    gpoHandle.value = 'none'
    isAuthenticated.value = false
    isTestUser.value = false

    const pinia = getActivePinia()
    if (pinia) {
      const storesToReset = ['order-history', 'cart', 'cart-suggestions', 'shopping-list', 'popular-store', 'private-cart']

      storesToReset.forEach((storeId) => {
        const store = (pinia as any)._s.get(storeId)
        if (store && store.$reset) {
          store.$reset()
        }
      })
    }

    return Promise.all([
      reloadAuth(),
    ])
  }

  const isPharmacyBannerVisible = computed(() =>
    isAuthenticated.value
    && userData.value
    && !isTestUser.value
    && !userData.value?.pharmacyRegistrationCertificateKey,
  )

  const hasHybridSearchFeature = computed(() =>
    !!userData.value?.featureKeys?.includes(HYBRID_SEARCH_FEATURE_KEY),
  )

  return {
    isAuthenticated,
    isTestUser,
    gpoHandle,
    data: userData,
    auth,
    isAuthPending,
    reloadAuth,
    isUserDataPending,
    $reset,
    load,
    login,
    register,
    update,
    getProfileData,
    forgotPassword,
    resetPassword,
    logout,
    updateIban,
    validateVatNumber,
    validateIban,
    accessToken,
    customerHandle,
    isPharmacyBannerVisible,
    hasHybridSearchFeature,
  }
})
